// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * An archive's version history, as a timeline beside the record (design doc §7.4, US-C-04).
 *
 * A timeline rather than a table because the shape of the data is a sequence, and because
 * this is the column a reader keeps one eye on while looking at something else — the
 * current assembly, a comparison, the maintenance log. A table would say the same thing in
 * more room and without the one property that matters here: that these versions follow one
 * another.
 *
 * Each entry is a complete snapshot, not a delta (§7.4), so any of them can be exported on
 * its own without replaying the ones before it.
 *
 * Only the two ends of a life are labelled — 建档 on v1, 报废 on the scrap. Everything in
 * between is a maintenance version, and a MAINTENANCE badge on each of them said nothing
 * the reason under it did not.
 *
 * The "quantity only" badge appears on maintenance versions alone. `snapshot_unchanged` is
 * also true of a scrap — retiring a machine does not touch its assembly — but US-C-04's
 * wording is about a like-for-like swap, and on a scrap it would describe the one thing
 * that did not happen.
 *
 * A `MODULE` version (component design §9.4) changed nothing on this archive itself: a
 * module fitted in it changed, and this version pins the module's new one. It says which
 * module moved to which version, and the change below that caused it, with links down.
 *
 * A version made by a maintenance record shows it in a floating card: hovering its labels
 * opens the card beside the timeline — the archive's own record for a `MAINTENANCE`
 * version, the module's record that caused it for a `MODULE` one. The pointer can move
 * into the card to read it or follow its links; clicking the labels pins it open, and
 * Escape or a click elsewhere closes it. The timeline itself looks as it did. Filing and
 * scrap have no record and do not open.
 */

import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "@/api/endpoints";
import type { ArchiveVersion } from "@/api/types";
import { useCursorList } from "@/hooks/useCursorList";
import { ChangeSummary, changeText } from "./ChangeType";
import { ErrorBanner } from "./ErrorBanner";
import { JiraLink } from "./JiraLink";
import { MaintenanceRecordPanel } from "./MaintenanceRecordPanel";
import { Pager } from "./Pager";

export function VersionTimeline({
  archiveId,
  currentVersion,
}: {
  archiveId: string;
  /** The archive's current version, marked in green — unless it is the scrap, which
   *  keeps its red. */
  currentVersion: number;
}) {
  const { t } = useTranslation();
  const versions = useCursorList(["archive-versions", archiveId], (cursor) =>
    api.archives.versions(archiveId, { cursor, limit: 20 }),
  );

  return (
    <aside className="record-side">
      <h2>{t("archive.versions")}</h2>
      <ErrorBanner error={versions.error} />

      <ol className="timeline">
        {versions.items.map((version) => {
          const record = recordOf(archiveId, version);
          const labels = (
            <>
              <span className="badge badge-version">v{version.version_no}</span>
              {version.source === "CREATE" && (
                <span className="badge">{t("archive.versionCreated")}</span>
              )}
              {version.source === "SCRAP" && (
                <span className="badge badge-scrapped">{t("archive.versionScrapped")}</span>
              )}
              {version.snapshot_unchanged && version.source === "MAINTENANCE" && (
                <span className="badge badge-unchanged">{t("maintenance.unchangedBadge")}</span>
              )}
            </>
          );
          return (
            <li
              key={version.version_no}
              className={[
                "timeline-item",
                `source-${version.source.toLowerCase()}`,
                version.version_no === currentVersion && version.source !== "SCRAP" && "is-latest",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {record ? (
                <RecordHoverCard
                  archiveId={record.archiveId}
                  eventId={record.eventId}
                  version={version}
                  label={t("archive.versionRecord", { version: version.version_no })}
                >
                  {labels}
                </RecordHoverCard>
              ) : (
                <div className="timeline-head">{labels}</div>
              )}
              <time className="timeline-time">
                {new Date(version.effective_at).toLocaleString()}
              </time>
              {version.source === "MODULE" ? (
                <div className="timeline-module">
                  {(version.module_changes ?? []).map((change) => (
                    <p key={change.position_code}>
                      {change.after ? (
                        <>
                          {t("archive.moduleUpdated", {
                            module: change.after.module_type ?? "",
                            version: change.after.version_no,
                          })}{" "}
                          <Link to={`/archives/${change.after.archive_id}`}>
                            {change.after.archive_no}
                          </Link>
                        </>
                      ) : (
                        change.position_name
                      )}
                    </p>
                  ))}
                  {version.caused_by && (
                    <p className="hint">
                      {t("archive.causedBy")}{" "}
                      <Link to={`/archives/${version.caused_by.archive_id}`}>
                        {version.caused_by.archive_no}
                      </Link>{" "}
                      v{version.caused_by.result_version}
                      {" · "}
                      {changeText(t, version.caused_by.change_type, version.caused_by.reason)}
                      {version.caused_by.submitted_by_email &&
                        ` · ${version.caused_by.submitted_by_email}`}{" "}
                      <JiraLink url={version.jira_url} />
                    </p>
                  )}
                </div>
              ) : (
                (version.change_type || version.reason || version.jira_url) && (
                  <p className="timeline-reason">
                    <ChangeSummary changeType={version.change_type} reason={version.reason} />{" "}
                    <JiraLink url={version.jira_url} />
                  </p>
                )
              )}
            </li>
          );
        })}
      </ol>

      <Pager
        hasNextPage={versions.hasNextPage}
        isFetchingNextPage={versions.isFetchingNextPage}
        onMore={() => void versions.fetchNextPage()}
      />
    </aside>
  );
}

/** The maintenance record behind a version, if one made it. */
function recordOf(
  archiveId: string,
  version: ArchiveVersion,
): { archiveId: string; eventId: string } | null {
  if (version.source === "MAINTENANCE" && version.event_id) {
    return { archiveId, eventId: version.event_id };
  }
  if (version.source === "MODULE" && version.caused_by) {
    return { archiveId: version.caused_by.archive_id, eventId: version.caused_by.event_id };
  }
  return null;
}

/** How long the pointer rests on the labels before the card opens, and how long it may be
 *  away (crossing to the card) before it closes. */
const OPEN_DELAY_MS = 150;
const CLOSE_DELAY_MS = 250;

function RecordHoverCard({
  archiveId,
  eventId,
  version,
  label,
  children,
}: {
  archiveId: string;
  eventId: string;
  version: ArchiveVersion;
  label: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);

  const later = (action: () => void, delay: number) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(action, delay);
  };
  const close = () => {
    window.clearTimeout(timer.current);
    setOpen(false);
    setPinned(false);
  };

  useEffect(() => () => window.clearTimeout(timer.current), []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!anchor.current?.contains(event.target as Node)) close();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover only previews; the button inside is the control
    <div
      className="timeline-hover"
      ref={anchor}
      onMouseEnter={() => later(() => setOpen(true), OPEN_DELAY_MS)}
      onMouseLeave={() => {
        if (pinned) window.clearTimeout(timer.current);
        else later(() => setOpen(false), CLOSE_DELAY_MS);
      }}
    >
      <button
        type="button"
        className="timeline-head timeline-link"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          if (pinned) {
            close();
          } else {
            window.clearTimeout(timer.current);
            setOpen(true);
            setPinned(true);
          }
        }}
      >
        {children}
      </button>
      {open && (
        <div className="timeline-popover" role="dialog" aria-label={label}>
          <MaintenanceRecordPanel
            archiveId={archiveId}
            eventId={eventId}
            recordNo={version.record_no}
            fromVersion={version.base_version_no}
            toVersion={version.version_no}
          />
        </div>
      )}
    </div>
  );
}
